import type { IntlShape } from 'react-intl/src/types';

import type { ContentfulHeadingFragment } from '@/graphql/contentful/api';
import type { DaySchedule, WorkingHours } from '@/integrations/algolia/types';
import type { ContentfulFixedPageCMSData } from '@/integrations/contentful/types';
import { serviceMessages } from '@/messages/service';

export function calculateHoursAndHolidays(hoursData: WorkingHours) {
  const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const formatTime12Hour = (time24: any) => {
    // eslint-disable-next-line prefer-const
    let [hours, minutes] = time24.split(':');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${hours}${minutes === '00' ? '' : `:${minutes}`}${ampm}`;
  };

  const weekdayHours = weekdays.map((day) => ({
    day,
    hours: (hoursData[day as keyof WorkingHours] as DaySchedule)
      ?.openIntervals[0]
      ? `${formatTime12Hour(
          (hoursData[day as keyof WorkingHours] as DaySchedule).openIntervals[0]
            .start,
        )}-${formatTime12Hour(
          (hoursData[day as keyof WorkingHours] as DaySchedule).openIntervals[0]
            .end,
        )}`
      : 'closed',
  }));

  const allSame = weekdayHours.every(
    (val, _i, arr) => val.hours === arr[0].hours,
  );
  const displayHours = allSame
    ? `Mo-Fri: ${weekdayHours[0].hours}`
    : weekdayHours.reduce((acc, curr) => {
        if (curr.hours !== weekdayHours[0].hours) {
          const dayInitial = `${curr.day.charAt(0).toUpperCase()}${curr.day
            .charAt(1)
            .toLowerCase()}`;
          return `${acc}, ${dayInitial}: ${curr.hours}`;
        }
        return acc;
      }, `Mo-Fri: ${weekdayHours[0].hours}`);

  const now = new Date();
  const upcomingHolidays =
    hoursData.holidayHours && hoursData.holidayHours.length > 0
      ? hoursData.holidayHours
          .filter((holiday) => {
            const holidayDate = new Date(holiday.date);
            const timeDiff = holidayDate.getTime() - now.getTime();
            const dayDiff = timeDiff / (1000 * 3600 * 24);
            return dayDiff >= -1 && dayDiff <= 15;
          })
          .map((holiday) => {
            const holidayDate = new Date(holiday.date);
            return holidayDate.toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
            });
          })
      : [];

  const weekendHours = {
    saturday: hoursData.saturday?.isClosed ? 'closed' : 'open',
    sunday: hoursData.sunday?.isClosed ? 'closed' : 'open',
  };
  const formattedWeekendHours = `Sat - Sun: ${
    weekendHours.saturday === 'closed' && weekendHours.sunday === 'closed'
      ? 'closed'
      : 'open'
  }`;

  return {
    displayHours,
    upcomingHolidays,
    formattedWeekendHours,
  };
}
export const getServiceContactHeadingText = (
  cms: ContentfulFixedPageCMSData | undefined,
  formatMessage: IntlShape['formatMessage'],
) => {
  const heading = cms?.topContent?.find(
    (content): content is ContentfulHeadingFragment =>
      content.__typename === 'Heading',
  );
  return heading?.text ?? formatMessage(serviceMessages.contactUs);
};
